import { Product } from "../data/products";
import { getCategoryBySlug } from "../data/categories";
import { brandConfig } from "../config/brandConfig";
import { Helmet } from "react-helmet-async";

type ProductSEOProps = {
  product: Product;
  productsRelatedTo: Product[];
};

export function ProductSEO({ product, productsRelatedTo }: ProductSEOProps) {
  const { NDD, brandName } = brandConfig;
  const {
    name,
    images,
    short,
    sku,
    slug,
    revendeurs,
    specs,
    ficheTechnique,
    categorySlug,
  } = product;
  const prices = revendeurs.map((r) => r.prix);
  const lowPrice = prices.length ? Math.min(...prices) : undefined;
  const highPrice = Math.max(...prices);
  const hasOffers = revendeurs.length > 0 && lowPrice !== undefined;
  const categoryName = getCategoryBySlug(categorySlug)?.categoryName ?? "";
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${NDD}/produits/${slug}#product`,
    name: name,
    image: images.map((img: string) => `${NDD}${img}`),
    description: short,
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    manufacturer: {
      "@type": "Organization",
      "@id": `${NDD}/#organization`,
    },
    sku: sku,
    category: categoryName,
    ...(hasOffers && {
      offers: {
        "@type": "AggregateOffer",
        url: `${NDD}/produits/${slug}`,
        priceCurrency: "EUR",
        lowPrice,
        highPrice,
        offerCount: revendeurs.length,
        availability: "https://schema.org/InStock",
      },
    }),
    additionalProperty: Object.entries(specs).map(([key, value]) => ({
      "@type": "PropertyValue",
      name: key,
      value: value,
    })),
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      returnPolicyCategory:
        "https://schema.org/MerchantReturnFiniteReturnWindow",
    },
    ...(ficheTechnique && {
      subjectOf: {
        "@type": "DigitalDocument",
        name: `Fiche technique ${name}`,
        url: `${NDD}${ficheTechnique}`,
      },
    }),
    isRelatedTo: productsRelatedTo.map(
      ({ slug: relatedSlug, name: relatedName }) => ({
        "@type": "Product",
        "@id": `${NDD}/produits/${relatedSlug}#product`,
        name: relatedName,
      })
    ),
  };
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
    </Helmet>
  );
}
